import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFlagValue } from '@labrav/flags'
import type { AxiosError } from 'axios'
import { camelize } from 'casing'
import type { Dispatch } from 'redux'

import { API } from '../../api'
import { startLoading, stopLoading } from '../../modules/loading/actions'
import { LoadingTypes } from '../../modules/loading/reducer'
import type { UserProfile } from '../../modules/userProfile/userProfileTypes'
import { AuthProvider } from '../../modules/userProfile/userProfileTypes'
import { PartnerType } from '../../types/partner'
import { actionTypeWrapper } from '../actionTypeWrapper'
import { engineServiceUrl, identityServiceUrl } from '../constants'
import { getErrorMessages } from '../error'
import { errorLogger, type LoggerGlobalState } from '../errorLogger'
import { RequestFailureMessage } from '../messagesContants'
import { InvitationStatus, type Role } from '../roles'

/** Matches host userList `UserListActionType.SET_USERS`. */
const SET_USERS = 'SET_USERS'
/** Matches host userList `UserListActionType.SET_ERROR_MESS`. */
const SET_ERROR_MESS = 'SET_ERROR_MESS'

export type UserStatus = 'ACTIVE' | 'PENDING' | 'BLOCKED'

export interface UserState {
  id: string
  name: string
  lastActivity: string | null
  status: UserStatus
  email: string
  password?: string
  idpUserId: string
  phone?: string
  roles: Role[]
  invitationStatus?: InvitationStatus
  createdAt: string
  firstName?: string
  lastName?: string
}

export interface UserList {
  users: UserState[]
  count: number
}

export interface UserListState {
  userList: UserList
  user: UserState | null
  userStateForBE: Partial<UserState>
  pageNumber: number
  pageSize: number
  noOfPage: number
  errMess: string
  isUserSidePanelOpen: boolean
  userLoader: boolean
}

type GetUsersParams = {
  partnerId: string
  pageNumber?: number
  pageSize?: number
  authProvider: AuthProvider
}

type IdentityServiceUserRow = {
  id?: string
  firstname?: string
  lastname?: string
  email?: string
  idp_user_id?: string
  roles?: Role[]
  status?: string
  invitation_status?: string
  created_at?: string
}

type PartnerTypeArg = PartnerType | `${PartnerType}`

type UseGetUsersRootState = {
  AccountData?: Partial<
    Record<PartnerType, { partnerData?: { partnerId?: string } }>
  >
  userProfile?: { userProfile?: UserProfile }
  userList?: Partial<Record<PartnerType, UserListState>>
}

const EMPTY_USERS: UserState[] = []

const emptyUserListState: UserListState = {
  userList: {
    users: [],
    count: 0,
  },
  userStateForBE: {},
  user: null,
  pageNumber: 1,
  pageSize: 10,
  noOfPage: 0,
  errMess: '',
  isUserSidePanelOpen: false,
  userLoader: false,
}

const mapIdentityServiceUser = (user: IdentityServiceUserRow): UserState => ({
  id: user?.id || '',
  name: `${user?.firstname || ''} ${user?.lastname || ''}`,
  lastActivity: null,
  email: user?.email || '',
  idpUserId: user?.idp_user_id || '',
  roles: user?.roles || [],
  status: user?.status as UserStatus,
  invitationStatus: user?.invitation_status as InvitationStatus | undefined,
  createdAt: user?.created_at || '',
  firstName: user?.firstname || '',
  lastName: user?.lastname || '',
})

const fetchUsers = ({ partnerId, pageSize, pageNumber }: GetUsersParams) =>
  API.get(`${engineServiceUrl}/partners/${partnerId}/users`, {
    params: {
      limit: pageSize,
      page_number: pageNumber,
    },
  })

const fetchUsersFromIdentityService = ({
  partnerId,
  pageSize,
  pageNumber,
}: GetUsersParams) =>
  API.get(`${identityServiceUrl}/partners/${partnerId}/users`, {
    params: {
      take: pageSize,
      skip: ((pageNumber || 1) - 1) * (pageSize || 10),
    },
  })

const setUsers = (
  userList: UserList,
  partnerType: PartnerTypeArg,
  pageSize?: number,
  pageNumber?: number
) =>
  actionTypeWrapper(partnerType, {
    type: SET_USERS,
    payload: { userList: camelize(userList), pageSize, pageNumber },
  })

const setUsersErrorMess = (message: string, partnerType: PartnerTypeArg) =>
  actionTypeWrapper(partnerType, {
    type: SET_ERROR_MESS,
    payload: message,
  })

const getPartnerUsers =
  (args: GetUsersParams, partnerObjectType: PartnerTypeArg) =>
  async (dispatch: Dispatch, getState: () => unknown) => {
    try {
      const { pageNumber, pageSize } = args
      dispatch(startLoading(LoadingTypes.GET_USERS))
      let responseData: UserList
      if (args.authProvider === AuthProvider.AUTH0) {
        const { data } = await fetchUsersFromIdentityService(args)
        responseData = {
          count: data?.metadata?.total || 0,
          users:
            data?.data?.map((user: IdentityServiceUserRow) =>
              mapIdentityServiceUser(user)
            ) || [],
        }
      } else {
        const { data } = await fetchUsers(args)
        responseData = {
          count: data?.count || 0,
          users: data?.users || [],
        }
      }
      dispatch(
        setUsers(
          responseData,
          partnerObjectType,
          pageSize as number,
          pageNumber as number
        )
      )
    } catch (err: unknown) {
      const errMess = getErrorMessages([RequestFailureMessage])(
        (err as AxiosError<ErrorResponse>).response
      )
      errorLogger({ globalState: getState() as LoggerGlobalState })(
        err as Error
      )
      dispatch(setUsersErrorMess(errMess, partnerObjectType))
    } finally {
      dispatch(stopLoading(LoadingTypes.GET_USERS))
    }
  }

/**
 * useGetUsers: is coupled with the pagination and the users state for the table on User Settings
 */
export const useGetUsers = (partnerType: PartnerTypeArg = PartnerType.User) => {
  const { partnerData, user, users, userListState } = useSelector(
    (state: UseGetUsersRootState) => ({
      partnerData: state.AccountData?.[partnerType]?.partnerData,
      user: state.userProfile?.userProfile,
      users: state.userList?.[partnerType]?.userList?.users ?? EMPTY_USERS,
      userListState: state.userList?.[partnerType] ?? emptyUserListState,
    })
  )

  const dispatch = useDispatch()
  const { loading: isFlagsLoading, value: auth0Enabled } = useFlagValue('auth0')

  const loading = Boolean(!partnerData || !user || users.length === 0)
  const authProvider = useMemo(
    () =>
      auth0Enabled && !isFlagsLoading ? AuthProvider.AUTH0 : AuthProvider.OKTA,
    [auth0Enabled, isFlagsLoading]
  )
  const partnerId = partnerData?.partnerId
  const { pageNumber, pageSize } = userListState

  useEffect(() => {
    if (partnerId && !isFlagsLoading) {
      dispatch(
        getPartnerUsers(
          {
            partnerId,
            authProvider,
            pageNumber: pageNumber || 1,
            pageSize: pageSize || 10,
          },
          partnerType
        ) as never
      )
    }
  }, [
    partnerId,
    authProvider,
    pageNumber,
    pageSize,
    isFlagsLoading,
    dispatch,
    partnerType,
  ])
  return { loading, users, userListState }
}

/**
 * useGetAllUsers: is not coupled with the table and will return all the users even if it needs to do multiple calls
 */
export const useGetAllUsers = (
  partnerType: PartnerTypeArg = PartnerType.User
) => {
  const { partnerData, user } = useSelector((state: UseGetUsersRootState) => ({
    partnerData: state.AccountData?.[partnerType]?.partnerData,
    user: state.userProfile?.userProfile,
  }))

  const [users, setUsersState] = useState<UserState[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const fetchingRef = useRef<boolean>(false)
  const partnerId = partnerData?.partnerId
  const pageSize = 100

  useEffect(() => {
    const fetchAllUsers = async () => {
      if (!partnerId || !user || fetchingRef.current) {
        if (!partnerId || !user) {
          setLoading(false)
        }
        return
      }

      fetchingRef.current = true
      setLoading(true)
      setUsersState([])

      try {
        let currentPage = 1
        let totalCount = 0
        const accumulatedUsers: UserState[] = []

        do {
          const { data } = await API.get(
            `${identityServiceUrl}/partners/${partnerId}/users`,
            {
              params: {
                take: pageSize,
                skip: (currentPage - 1) * pageSize,
              },
            }
          )

          const fetchedUsers: UserState[] =
            data?.data?.map((row: IdentityServiceUserRow) =>
              mapIdentityServiceUser(row)
            ) || []

          totalCount = data?.metadata?.total || 0
          accumulatedUsers.push(...fetchedUsers)

          const totalPages = Math.ceil(totalCount / pageSize)
          currentPage++

          if (currentPage > totalPages || fetchedUsers.length === 0) {
            break
          }
        } while (accumulatedUsers.length < totalCount)

        setUsersState(accumulatedUsers)
      } catch (error) {
        console.error('Error fetching all users:', error)
        setUsersState([])
      } finally {
        setLoading(false)
        fetchingRef.current = false
      }
    }

    fetchingRef.current = false
    fetchAllUsers()

    return () => {
      fetchingRef.current = false
    }
  }, [partnerId, user, partnerType])

  return { loading, users }
}
